import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { SlashCommandList, SlashCommandListRef, SlashCommandItem } from './SlashCommandList';

// Full command list — filtered by query in items()
export const allSlashCommands = (onImage: () => void, onLink: () => void, onTable: () => void, onEmoji: () => void): SlashCommandItem[] => [
  {
    id: 'heading1',
    title: 'כותרת 1',
    description: 'כותרת ראשית גדולה',
    icon: 'format_h1',
    keywords: ['heading', 'h1', 'כותרת'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleHeading({ level: 1 }).run(),
  },
  {
    id: 'heading2',
    title: 'כותרת 2',
    description: 'כותרת משנית',
    icon: 'format_h2',
    keywords: ['heading', 'h2'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleHeading({ level: 2 }).run(),
  },
  {
    id: 'heading3',
    title: 'כותרת 3',
    description: 'כותרת קטנה',
    icon: 'format_h3',
    keywords: ['heading', 'h3'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleHeading({ level: 3 }).run(),
  },
  {
    id: 'bulletList',
    title: 'רשימה',
    description: 'רשימה עם נקודות',
    icon: 'format_list_bulleted',
    keywords: ['list', 'bullet', 'רשימה'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleBulletList().run(),
  },
  {
    id: 'orderedList',
    title: 'רשימה ממוספרת',
    description: 'רשימה עם מספרים',
    icon: 'format_list_numbered',
    keywords: ['list', 'numbered', 'ordered'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleOrderedList().run(),
  },
  {
    id: 'taskList',
    title: 'רשימת משימות',
    description: 'checkboxes לסימון',
    icon: 'checklist',
    keywords: ['task', 'todo', 'checklist', 'משימות'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleTaskList().run(),
  },
  {
    id: 'blockquote',
    title: 'ציטוט',
    description: 'טקסט מצוטט',
    icon: 'format_quote',
    keywords: ['quote', 'blockquote', 'ציטוט'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleBlockquote().run(),
  },
  {
    id: 'codeBlock',
    title: 'בלוק קוד',
    description: 'קטע קוד עם highlighting',
    icon: 'code',
    keywords: ['code', 'codeblock', 'קוד'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).toggleCodeBlock().run(),
  },
  {
    id: 'divider',
    title: 'קו מפריד',
    description: 'קו אופקי',
    icon: 'horizontal_rule',
    keywords: ['divider', 'hr', 'rule', 'קו', 'מפריד'],
    run: (editor) => editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).setHorizontalRule().run(),
  },
  {
    id: 'table',
    title: 'טבלה',
    description: 'טבלה עם שורות ועמודות',
    icon: 'table_chart',
    keywords: ['table', 'טבלה'],
    run: (editor) => {
      editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
      onTable();
    },
  },
  {
    id: 'image',
    title: 'תמונה',
    description: 'העלה תמונה',
    icon: 'image',
    keywords: ['image', 'img', 'photo', 'picture', 'תמונה'],
    run: (editor) => {
      editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
      onImage();
    },
  },
  {
    id: 'link',
    title: 'קישור',
    description: 'הוסף קישור',
    icon: 'link',
    keywords: ['link', 'url', 'קישור'],
    run: (editor) => {
      editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
      onLink();
    },
  },
  {
    id: 'emoji',
    title: 'אימוג\'י',
    description: 'הוסף סמל',
    icon: 'mood',
    keywords: ['emoji', 'smile', 'אימוג'],
    run: (editor) => {
      editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
      onEmoji();
    },
  },
  {
    id: 'youtube',
    title: 'YouTube',
    description: 'הטמע סרטון מ-YouTube',
    icon: 'smart_display',
    keywords: ['youtube', 'video', 'סרטון'],
    run: (editor) => {
      const url = window.prompt('הדבק קישור YouTube:');
      editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
      if (url) {
        editor.chain().focus().setYoutubeVideo({ src: url, width: 560, height: 315 }).run();
      }
    },
  },
];

interface SlashCommandOptions {
  onImage: () => void;
  onLink: () => void;
  onTable: () => void;
  onEmoji: () => void;
}

export const SlashCommand = (options: SlashCommandOptions) =>
  Extension.create({
    name: 'slashCommand',
    addOptions() {
      return {
        suggestion: {
          char: '/',
          startOfLine: false,
          command: ({ editor, range, props }: any) => {
            props.run(editor, range);
          },
        },
      };
    },
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          ...this.options.suggestion,
          items: ({ query }) => {
            const commands = allSlashCommands(options.onImage, options.onLink, options.onTable, options.onEmoji);
            if (!query) return commands;
            const q = query.toLowerCase();
            return commands.filter(
              (c) => c.title.toLowerCase().includes(q) || c.keywords.some((k) => k.toLowerCase().includes(q))
            );
          },
          render: () => {
            let component: ReactRenderer<SlashCommandListRef> | null = null;
            let popup: TippyInstance[] | null = null;

            return {
              onStart: (props: any) => {
                component = new ReactRenderer(SlashCommandList, { props, editor: props.editor });
                if (!props.clientRect) return;
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  theme: 'tt-slash',
                  arrow: false,
                  offset: [0, 4],
                });
              },
              onUpdate: (props: any) => {
                component?.updateProps(props);
                if (!props.clientRect || !popup) return;
                popup[0].setProps({ getReferenceClientRect: props.clientRect });
              },
              onKeyDown: (props: { event: KeyboardEvent }) => {
                if (props.event.key === 'Escape') {
                  popup?.[0].hide();
                  return true;
                }
                return component?.ref?.onKeyDown(props) || false;
              },
              onExit: () => {
                popup?.[0].destroy();
                component?.destroy();
                popup = null;
                component = null;
              },
            };
          },
        }),
      ];
    },
  });
